import { styled } from '@mui/material/styles';
import { CardContent, Typography, Box, Grid, Checkbox, TableCell, TableContainer } from '@mui/material';

interface ScenarioCardContainerProps {
  status: string;
}

export const ScenarioCardContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'status' && prop !== 'isChecked',
})<ScenarioCardContainerProps & { isChecked: boolean }>(({ theme, status, isChecked }) => ({
  '.MuiCard-root': {
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 400,
    backgroundColor: status === 'complete' ? '#d4edda' : status === 'draft' ? '#E6ECF0' : '#FFF6E0',
    width: '374.78px',
    height: '392.93px',
    boxShadow:
      status === 'complete'
        ? '0 4px 8px rgba(0, 128, 0, 0.2), 0 2px 4px rgba(0, 128, 0, 0.15)'
        : status === 'draft'
        ? '0 4px 8px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)'
        : '0 4px 8px rgba(255, 165, 0, 0.3), 0 2px 4px rgba(255, 165, 0, 0.15)',
    transition: 'box-shadow 0.3s ease, transform 0.2s ease, border 0.2s ease',
    border: isChecked ? '2px solid red' : '2px solid transparent',
    '&:hover': {
      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)',
      transform: 'translateY(-4px)',
    },
  },
  margin: '0 auto',
  hr: {
    border: 0,
    height: 1,
    background: '#e0e0e0',
    margin: '8px 0',
  },
}));

export const StyledCardContent = styled(CardContent)({
  flex: 1,
  overflowY: 'hidden',
  backgroundColor: 'white',
  padding: 0,
});

export const StyledCardHeaderBox = styled(Box)<ScenarioCardContainerProps>(({ status }) => ({
  backgroundColor: status === 'complete' ? '#D0FAE5' : status === 'draft' ? '#D7D7D7' : '#FFE5A3',
  width: '100%',
  height: '20.26px',
  borderRadius: '4px',
  padding: '4px 7px 0px 5px',
}));

export const StyledCardHeaderTitle = styled(Typography)({
  fontWeight: 600,
  fontSize: '14.52px',
  lineHeight: '17.7px',
  display: 'flex',
  alignItems: 'center',
});

export const StyledStatusText = styled(Typography)<ScenarioCardContainerProps>(({ status }) => ({
  color: status === 'complete' ? '#305236' : '#484A49',

  fontSize: '10.89px',
  fontWeight: 600,
  lineHeight: '13.27px',
  letterSpacing: '2%',
}));

export const StyledGridContainer = styled(Grid)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: 'white',
  height: '30px',
});

export const StyledGridLeft = styled(Grid)({
  display: 'flex',
  alignItems: 'center',
  marginLeft: '6px',
  marginRight: '6px',
});

export const StyledGridRight = styled(Grid)({
  display: 'flex',
  marginRight: '6px',
  justifyContent: 'space-around',
});

export const StyledUserName = styled(Typography)({
  fontSize: '10px',
  fontWeight: 600,
  lineHeight: '12.19px',
  color: '#DF2020',
  marginRight: '4px',
  marginTop: '2.5px',
});

export const StyledUserDate = styled(Typography)({
  fontSize: '8px',
  fontWeight: 500,
  lineHeight: '9.75px',
  color: '#7D7D7D',
  marginTop: '2.5px',
});

export const CompareText = styled(Typography)({
  fontSize: '10.89px',
  fontWeight: 600,
  lineHeight: '13.27px',
  marginRight: '10px',
  letterSpacing: '2%',
  marginTop: '2px',
});

export const StyledImage = styled('img')({
  marginRight: '8px',
  cursor: 'pointer',
});

export const StyledMetricTypography = styled(Typography)({
  fontWeight: 700,
  fontSize: '14px',
  lineHeight: '17.07px',
  color: '#02AD7B',
  marginLeft: '10px',
  whiteSpace: 'nowrap',
  display: 'flex',
  justifyContent: 'center',
});

export const StyledMetricBox = styled(Box)({
  marginTop: '8px',
  borderRadius: '4px',
  padding: '5px',
});

export const StyledMetricGrid = styled(Grid)({
  borderRight: '1px dotted #E6ECF0',
  borderBottom: '1px solid #E6ECF0',
  display: 'flex',
  alignItems: 'center',
  padding: '14px',
  '&:last-of-type': {
    borderBottom: 'none',
  },
});

export const StyledNoBorderGrid = styled(Grid)({
  borderBottom: '1px solid #E6ECF0',
  display: 'flex',
  alignItems: 'center',
  padding: '8px',
  '&:last-of-type': {
    borderBottom: 'none',
  },
});

export const StyledHeaderContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
});

export const StyledTagImage = styled('img')({
  marginLeft: '8px',
});

export const StyledMetricTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '14px',
  lineHeight: '17.07px',
  whiteSpace: 'nowrap',
});

export const StyledCheckbox = styled(Checkbox)(() => ({
  padding: 0,
  marginRight: '8px',
  '& .MuiSvgIcon-root': {
    width: '15.43px',
    height: '15.43px',
    borderRadius: '2.72px',
  },
}));

export const StyledTableContainer = styled(TableContainer)(() => ({
  maxWidth: '375.88px',
  width: '100%',
  height: '153px',
  overflowY: 'hidden',
  overflowX: 'hidden',
  '&:hover': {
    overflowY: ' auto',
    overflowX: ' auto',
  },
}));

export const StyledTableCell = styled(TableCell)(() => ({
  padding: 0,
  borderRight: '1px solid #E5E5E5',
  textAlign: 'center',
  height: '38.11px',
  wordBreak: 'break-word',
  width: '70px',
}));
